/**
 * Card Component con efecto de vidrio esmerilado (Glassmorphism)
 * Diseño minimalista inspirado en Viralistic
 */

import React, { useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { motion } from 'framer-motion';

interface GlassCardProps {
  title: string;
  description: string;
  features?: string[];
  ctaText?: string;
  ctaHref?: string;
  delay?: number;
  onCtaClick?: () => void;
  sx?: any; // Añadir prop sx opcional
}

const GlassCard: React.FC<GlassCardProps> = ({
  title,
  description,
  features = [],
  ctaText,
  ctaHref,
  delay = 0,
  onCtaClick,
  sx, // Recibir sx prop
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 50,
      scale: 0.9,
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        delay,
        ease: [0.25, 0.46, 0.45, 0.94] as const,
      },
    },
  };

  const handleCardClick = () => {
    if (onCtaClick) {
      onCtaClick();
    } else if (ctaHref && ctaHref.startsWith('#')) {
      const sectionId = ctaHref.substring(1);
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else if (ctaHref) {
      window.location.href = ctaHref;
    }
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ amount: 0.3, once: false }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={handleCardClick}
      style={{ cursor: (ctaHref || onCtaClick) ? 'pointer' : 'default', height: '100%' }} // height 100% para igualar alturas
    >
      <Box
        sx={{
          p: { xs: 5, md: 6 },
          borderRadius: 0,
          background: 'rgba(36, 44, 39, 0.6)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          border: '1px solid rgba(184, 197, 184, 0.15)',
          position: 'relative',
          overflow: 'hidden',
          minHeight: { xs: 'auto', md: '340px' }, // Aumentar un poco minHeight
          height: '100%', // Asegurar height 100%
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          transition: 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94), background 0.5s, border-color 0.5s, box-shadow 0.5s',
          boxShadow: '0 2px 15px rgba(0, 0, 0, 0.2)',
          willChange: 'transform',
          ...sx, // Aplicar estilos personalizados
            '&:hover': {
            transform: 'translateY(-8px)',
            background: 'rgba(47, 58, 51, 0.7)',
            border: '1px solid rgba(0, 225, 112, 0.5)', // #00e170 con opacidad
            boxShadow: '0 15px 40px rgba(0, 225, 112, 0.15)', // Sombra con tinte verde
          },
        }}
      >
        {/* Efecto de brillo interno */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: 'linear-gradient(90deg, transparent, #00e170, transparent)', // Línea verde neón
            opacity: isHovered ? 1 : 0.3,
            transition: 'opacity 0.4s ease',
            borderRadius: '4px 4px 0 0',
          }}
        />
        
        {/* Efecto de gradiente interno */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(184, 197, 184, 0.05) 0%, transparent 50%)',
            opacity: isHovered ? 1 : 0.5,
            transition: 'opacity 0.4s ease',
            borderRadius: 4,
          }}
        />

        {/* Contenido principal */}
        <Box sx={{ flex: 1, position: 'relative', zIndex: 2 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 400,
              mb: 3,
              color: '#B8C5B8',
              fontSize: { xs: '0.9375rem', md: '1rem' },
              lineHeight: 1.4,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >
            {title}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: '#D4E0D4',
              lineHeight: 1.7,
              mb: 4,
              fontSize: { xs: '0.875rem', md: '0.9375rem' },
              fontWeight: 400,
            }}
          >
            {description}
          </Typography>

          {/* Features list */}
          {features.length > 0 && (
            <Box sx={{ mb: 4 }}>
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isHovered ? { opacity: 1, x: 0 } : { opacity: 0.8, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#D4E0D4',
                      mb: 1.5,
                      fontSize: '0.8125rem',
                      fontWeight: 400,
                      position: 'relative',
                      pl: 2.5,
                      lineHeight: 1.6,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '0.6em',
                        width: '4px',
                        height: '4px',
                        borderRadius: '50%',
                        backgroundColor: '#B8C5B8',
                      },
                    }}
                  >
                    {feature}
                  </Typography>
                </motion.div>
              ))}
            </Box>
          )}
        </Box>

        {/* CTA Button */}
        {ctaText && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: delay + 0.3 }}
            style={{ position: 'relative', zIndex: 2 }}
          >
            <Button
              variant="outlined"
              size="medium"
              sx={{
                width: '100%',
                fontSize: '0.6875rem',
                px: 3,
                py: 1.5,
                borderRadius: 0,
                fontWeight: 400,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                borderColor: 'rgba(184, 197, 184, 0.4)',
                color: '#B8C5B8',
                borderWidth: '1px',
                background: 'transparent',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  backgroundColor: 'rgba(184, 197, 184, 0.12)',
                  color: '#FFFFFF',
                  borderColor: '#B8C5B8',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(184, 197, 184, 0.08), transparent)',
                  transition: 'left 0.5s ease',
                },
                '&:hover::before': {
                  left: '100%',
                },
                transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              }}
            >
              {ctaText}
            </Button>
          </motion.div>
        )}

        {/* Efecto de partículas flotantes */}
        <Box
          sx={{
            position: 'absolute',
            top: '15%',
            right: '12%',
            width: '3px',
            height: '3px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(184, 197, 184, 0.6) 0%, transparent 70%)',
            opacity: isHovered ? 1 : 0.4,
            transition: 'opacity 0.4s ease',
            boxShadow: '0 0 6px rgba(184, 197, 184, 0.3)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            left: '18%',
            width: '2px',
            height: '2px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(184, 197, 184, 0.4) 0%, transparent 70%)',
            opacity: isHovered ? 0.8 : 0.2,
            transition: 'opacity 0.4s ease',
            boxShadow: '0 0 4px rgba(184, 197, 184, 0.2)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '60%',
            right: '25%',
            width: '1px',
            height: '1px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(184, 197, 184, 0.3) 0%, transparent 70%)',
            opacity: isHovered ? 0.6 : 0.1,
            transition: 'opacity 0.4s ease',
            boxShadow: '0 0 3px rgba(184, 197, 184, 0.1)',
          }}
        />
      </Box>
    </motion.div>
  );
};

export default GlassCard;
